import { useEffect, useState } from "react";
import { Outlet } from "react-router-dom";
import { motion } from "framer-motion";
import { Sidebar } from "./Sidebar";
import { Topbar } from "./Topbar";
import { IdleWarning } from "../auth/IdleWarning";
import { useIdleTimer } from "@/lib/auth/idle-timer";
import { useAuthStore } from "@/lib/auth/store";
import { refreshAccessToken } from "@/lib/api/client";

export function AppShell() {
  const idle = useIdleTimer();

  // The access token lives in memory only, so after a hard reload we have a
  // refresh token but no access token. Mint a fresh access token before
  // rendering — otherwise authenticated (RBAC-gated) actions would silently
  // fail with "missing role" because no Authorization header is sent.
  const accessToken = useAuthStore((s) => s.accessToken);
  const refreshToken = useAuthStore((s) => s.refreshToken);
  const [booting, setBooting] = useState(!accessToken && !!refreshToken);

  useEffect(() => {
    if (!accessToken && refreshToken) {
      refreshAccessToken().finally(() => setBooting(false));
    } else {
      setBooting(false);
    }
    // Run once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (booting) {
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <div className="size-7 animate-spin rounded-full border-2 border-muted border-t-primary" />
      </div>
    );
  }

  // `h-screen` (not min-h-screen) + `overflow-hidden` lock the outer
  // shell to the viewport. Without this, long page bodies push the
  // whole layout taller than the viewport — the page scrolls, and the
  // sidebar scrolls with it. Now only the <main> region scrolls; the
  // sidebar and topbar stay pinned.
  return (
    <div className="flex h-screen overflow-hidden bg-background">
      <Sidebar />
      <div className="flex flex-1 flex-col min-w-0">
        <Topbar />
        <main className="flex-1 overflow-y-auto">
          <motion.div
            key="page"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.18, ease: "easeOut" }}
            className="mx-auto w-full max-w-7xl p-4 lg:p-8"
          >
            <Outlet />
          </motion.div>
        </main>
      </div>
      <IdleWarning
        open={idle.showWarning}
        onContinue={idle.dismissWarning}
        onLogout={idle.logoutNow}
      />
    </div>
  );
}
